import { motion } from 'framer-motion'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Paper, SearchField } from '../../primitives/index.js'
import { useTidalSandboxStateField } from '../atoms/tidalSandboxState.js'
import type { UpdateFunction } from '../hooks/useQueryState/useQueryState.js'
import { useResizeNavigation } from '../hooks/useResizeNavigation.js'
import { useSearchDialogVisibility } from '../hooks/useSearchDialogVisibility.js'
import { NavigationCollapsed } from './NavigationCollapsed.js'
import { NavigationControls } from './NavigationControls.js'
import { NavigationTree } from './NavigationTree.js'
import { ResizeNavigation } from './ResizeNavigation.js'
import { ScrollableContainer } from './ScrollableContainer.js'
import { SearchDialog } from './SearchDialog/SearchDialog.js'

export type Props = {
  sidebarPosition: 'row' | 'row-reverse'
  onChangeSidebarPosition: UpdateFunction<'row' | 'row-reverse'>
}

export function Navigation(props: Props) {
  const { sidebarPosition, onChangeSidebarPosition } = props

  const [treeHeight, setTreeHeight] = useState(0)

  const [navigationDisabled] = useTidalSandboxStateField('navigationDisabled')
  const [disableKeybindings] = useTidalSandboxStateField('disableKeybindings')

  const rSidebarPosition = useRef(sidebarPosition)
  useLayoutEffect(() => void (rSidebarPosition.current = sidebarPosition), [sidebarPosition])

  const [collapsed, setCollapsed] = useTidalSandboxStateField('navigationCollapsed')
  const rCollapsed = useRef(collapsed)
  useLayoutEffect(() => void (rCollapsed.current = collapsed), [collapsed])

  const [fullScreen, setFullScreen] = useTidalSandboxStateField('fullScreen')
  const rFullScreen = useRef(fullScreen)
  useLayoutEffect(() => void (rFullScreen.current = fullScreen), [fullScreen])

  const { mWidth, setNavigationWidth } = useResizeNavigation(sidebarPosition)

  const { onCloseSearchDialog, onShowSearchDialog, searchDialogVisible } = useSearchDialogVisibility()

  useEffect(() => {
    if (disableKeybindings) return

    function handleOnKeyDown(event: KeyboardEvent) {
      const fromAnyInput = event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement

      if (fromAnyInput) return

      const noModifiers = !event.metaKey && !event.ctrlKey && !event.altKey && !event.shiftKey

      if (event.key.toLowerCase() === 's' && noModifiers) {
        event.preventDefault()
        setCollapsed(!rCollapsed.current)
      }

      if (event.key.toLowerCase() === 'f' && noModifiers) {
        event.preventDefault()
        setFullScreen(!rFullScreen.current)
      }

      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        onShowSearchDialog()
      }
    }

    window.addEventListener('keydown', handleOnKeyDown)

    return () => {
      window.removeEventListener('keydown', handleOnKeyDown)
    }
  }, [setCollapsed, setFullScreen, onShowSearchDialog, disableKeybindings])

  if (fullScreen) {
    return (
      <SearchDialog navigationDisabled={navigationDisabled} onClose={onCloseSearchDialog} open={searchDialogVisible} />
    )
  }

  if (collapsed) {
    return <NavigationCollapsed setCollapsed={setCollapsed} sidebarPosition={sidebarPosition} />
  }

  return (
    <Paper data-testid="tidal-sandbox_navigation" elevation={3}>
      <NavigationControls
        onChangeSidebarPosition={onChangeSidebarPosition}
        setCollapsed={setCollapsed}
        setFullScreen={setFullScreen}
        sidebarPosition={sidebarPosition}
      />

      <motion.div
        style={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          minWidth: '390px',
          position: 'relative',
          width: mWidth,
        }}
      >
        <div style={{ padding: '8px' }}>
          <SearchField
            endAdornmentLabel="ctrl+k"
            onFocus={onShowSearchDialog}
            slotProps={{
              root: {
                onClick: () => {
                  onShowSearchDialog()
                },
              },
            }}
          />
        </div>

        <ScrollableContainer onChangeHeight={setTreeHeight}>
          <NavigationTree height={treeHeight} />
        </ScrollableContainer>
      </motion.div>

      <ResizeNavigation mWidth={mWidth} setNavigationWidth={setNavigationWidth} sidebarPosition={sidebarPosition} />

      <SearchDialog navigationDisabled={navigationDisabled} onClose={onCloseSearchDialog} open={searchDialogVisible} />
    </Paper>
  )
}
